import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { Input } from '@flipster/ui';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { SegmentPage } from '../../misc/segment/segment-page';
import { competitionBunja } from '../bunja';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const InviteCodeModal = ({ isOpen, onClose, onExit }: Props) => {
  const t = useTranslations();
  const [invitationCode, setInvitationCode] = useState('');
  const { id, registerCompetition, registerCompetitionErrorMessageAtom, resetRegisterCompetitionError } =
    useBunja(competitionBunja);
  const registrationErrorMessage = useAtomValue(registerCompetitionErrorMessageAtom);
  return (
    <>
      <SegmentPage name={CompetitionsEvent.CompetitionEnterCodeModalView} context={{ competition_id: id }} />
      <Modal isOpen={isOpen} onExit={onExit}>
        <Modal.Close onClose={onClose} />
        <Modal.Header>
          <Modal.Title label={t('Autogen_common.r251126.s_Join_with_an_invite_code')} />
        </Modal.Header>
        <Modal.Content css={stack.raw({ gap: 40 })}>
          <div
            className={hstack({
              gap: 8,
              p: 12,
              rounded: 8,
              textStyle: 'Text/M/Regular',
              color: 'content.accent.info',
              bg: 'background.accent.primary.tint',
            })}
          >
            <InformationCircleIcon size={16} />
            <p>
              <FormattedMessage id='Autogen_common.r251126.s_Use_the_code_shared_by_the_host_to_join' />
            </p>
          </div>
          <div className={stack({ gap: 12 })}>
            <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Invite_code' />
            </p>
            <div className={stack({ gap: 4 })}>
              <Input
                placeholder={t('Autogen_common.r251126.s_Enter_the_code_here')}
                value={invitationCode}
                onFocus={() => {
                  segment.track(CompetitionsEvent.CompetitionEnterCodeModalEnterCodeClick, { competition_id: id });
                }}
                onChange={(e) => {
                  setInvitationCode(e.currentTarget.value);
                  resetRegisterCompetitionError();
                }}
                isError={Boolean(registrationErrorMessage)}
              />
              {registrationErrorMessage && (
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.accent.sell' })}>
                  {registrationErrorMessage}
                </p>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button
            label={t('Autogen_common.r251126.s_Join')}
            withSpinner
            disabled={!invitationCode || Boolean(registrationErrorMessage)}
            onClick={async () => {
              try {
                segment.track(CompetitionsEvent.CompetitionEnterCodeModalJoinClick, { competition_id: id });
                await registerCompetition(invitationCode);
                onClose();
                snackbar.success({
                  message: <FormattedMessage id='Autogen_common.r251126.s_Your_registration_has_been_successful' />,
                });
              } catch (error) {
                snackbar.error({ message: getErrorMessage(error) });
                if (getErrorCode(error) !== 'api.tradingCompetition.userInvitationCodeNotMatched') {
                  onClose();
                }
              }
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};
